<template>
  <QueryLoading
    v-if="isProductsLoading || isCategoriesLoading"
  />
  <QueryError
    v-else-if="isProductsError || isCategoriesError"
    class="h-full"
  >
    Failed to load products
  </QueryError>
  <div v-else v-bind="containerProps" class="h-full p-3">
    <div v-bind="wrapperProps">
      <template
        v-for="item in renderedItems"
        :key="item.index"
      >
        <template v-if="item.data.type === 'searchInput'">
          <div class="flex flex-col px-3">
            <SearchInput v-model="searchString" />
          </div>
        </template>
        <template v-if="item.data.type === 'category'">
          <slot name="category" v-bind="item.data" />
        </template>
        <template v-else-if="item.data.type === 'product'">
          <slot name="product" v-bind="item.data" />
        </template>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Category } from "@/services/sharedTypes/categories"
import type { Product } from "@/services/sharedTypes/products"
import { useProducts } from "@/services/products/listProducts"
import { useCategories } from "@/services/categories/listCategories"
import { useVirtualList } from "@vueuse/core"

const {
  data: products,
  isError: isProductsError,
  isLoading: isProductsLoading,
} = useProducts()
const {
  data: categories,
  isError: isCategoriesError,
  isLoading: isCategoriesLoading,
} = useCategories()

type VirtualListItemProduct = {
  type: "product"
  id: string
  product: Product
}

type VirtualListItemCategory = {
  type: "category"
  id: string
  category: Category
}

type VirtualListItemSearch = {
  type: "searchInput"
}

type VirtualListItem =
  | VirtualListItemProduct
  | VirtualListItemCategory
  | VirtualListItemSearch

const searchString = ref<string>("")

const virtualList = computed<VirtualListItem[]>(() => {
  const filteredProducts = products.value?.filter(
    (product) =>
      product.name
        .toLowerCase()
        .includes(searchString.value.toLowerCase())
  )

  const productsAndCategories = Array.isArray(
    filteredProducts
  )
    ? categories.value.reduce<VirtualListItem[]>(
        (accumulator, category) => {
          const productsForCategory = filteredProducts
            .filter(
              ({ parentCategoryId }) =>
                parentCategoryId === category.id
            )
            .map((product) => ({
              type: "product",
              id: `product:${product.id}`,
              product: product,
            }))

          if (productsForCategory.length === 0)
            return accumulator

          return [
            ...accumulator,
            ...(category.id && searchString.value === ""
              ? [
                  {
                    type: "category",
                    id: `category:${category.id}`,
                    category: category,
                  },
                ]
              : []),
            ...productsForCategory,
          ] as VirtualListItem[]
        },
        filteredProducts
          .filter(
            ({ parentCategoryId }) =>
              parentCategoryId === null
          )
          .map((product) => ({
            type: "product",
            id: `product:${product.id}`,
            product: product,
          })) as VirtualListItem[]
      )
    : []

  return [{ type: "searchInput" }, ...productsAndCategories]
})

const calculateItemHeight = (index: number) => {
  const searchInputHeight = 38
  const containerPadding = 12
  const type = virtualList.value[index].type
  switch (type) {
    case "category":
      return 40
    case "product":
      return 80
    case "searchInput":
      // Adjustment required for scrollTo(1) to work
      return searchInputHeight + containerPadding
    default:
      return 0
  }
}

const {
  list: renderedItems,
  containerProps,
  wrapperProps,
  scrollTo,
} = useVirtualList(virtualList, {
  itemHeight: calculateItemHeight,
  overscan: 5,
})

onMounted(() => {
  // Initially hide search bar
  scrollTo(1)
})
</script>
